<template>
  <div v-if="article" class="article-page">
    <section class="article-hero">
      <div class="article-breadcrumbs">
        <router-link to="/">Головна</router-link>
        <span>/</span>
        <router-link to="/blog">Блог</router-link>
      </div>
      <div class="article-date">{{ article.datePublished }}</div>
      <h1>{{ article.h1 }}</h1>
      <p class="article-excerpt">{{ article.excerpt }}</p>

      <div class="article-keywords">
        <span v-for="keyword in article.keywords" :key="keyword" class="keyword-chip">{{ keyword }}</span>
      </div>
    </section>

    <section class="article-content">
      <article v-for="section in article.sections" :key="section.title" class="content-card">
        <h2>{{ section.title }}</h2>
        <p>{{ section.body }}</p>
      </article>
    </section>

    <section class="article-faq">
      <h2>Питання по темі</h2>
      <div class="faq-grid">
        <article v-for="item in article.faq" :key="item.q" class="faq-card">
          <h3>{{ item.q }}</h3>
          <p>{{ item.a }}</p>
        </article>
      </div>
    </section>

    <section class="article-links">
      <div class="article-links-box">
        <div class="article-links-head">
          <h2>Повʼязані сторінки послуг</h2>
        </div>
        <div class="article-link-grid">
          <router-link
            v-for="landing in relatedServicePages"
            :key="landing.slug"
            :to="landing.path"
            class="related-card"
          >
            <strong>{{ landing.h1 }}</strong>
            <span>{{ landing.description }}</span>
          </router-link>
        </div>
      </div>

      <div class="article-links-box">
        <div class="article-links-head">
          <h2>Інші статті</h2>
          <router-link to="/blog" class="article-more-link">Усі статті</router-link>
        </div>
        <div class="article-link-grid">
          <router-link
            v-for="relatedArticle in relatedArticles"
            :key="relatedArticle.slug"
            :to="relatedArticle.path"
            class="related-card"
          >
            <strong>{{ relatedArticle.h1 }}</strong>
            <span>{{ relatedArticle.excerpt }}</span>
          </router-link>
        </div>
      </div>
    </section>

    <section class="article-cta">
      <div>
        <h2>Потрібен ремонт прямо зараз?</h2>
        <p>Залишайте заявку онлайн або напишіть у Telegram, і менеджер швидко звʼяжеться з вами.</p>
      </div>
      <div class="article-cta-actions">
        <router-link to="/orders/create" class="cta-btn cta-btn-primary">Замовити ремонт</router-link>
        <a href="tel:+380682772822" class="cta-btn cta-btn-secondary">Подзвонити</a>
      </div>
    </section>
  </div>
</template>

<script>
import { computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { blogArticleMap, blogArticles } from '../data/blogArticles';
import { serviceLandings } from '../data/serviceLandings';
import { applySeo, localBusinessJsonLd } from '../utils/seo';
import { articleJsonLd, breadcrumbJsonLd, faqJsonLd } from '../utils/schema';

export default {
  name: 'BlogArticlePage',
  setup() {
    const route = useRoute();
    const article = computed(() => blogArticleMap[route.params.slug] || null);

    const relatedServicePages = computed(() => {
      const currentArticle = article.value;
      if (!currentArticle) return [];

      return (currentArticle.relatedServices || [])
        .map((slug) => serviceLandings[slug])
        .filter(Boolean);
    });

    const relatedArticles = computed(() => {
      const currentArticle = article.value;
      if (!currentArticle) return [];

      return blogArticles
        .filter((item) => item.slug !== currentArticle.slug)
        .slice(0, 3);
    });

    watch(
      article,
      (value) => {
        if (!value) return;

        applySeo({
          title: value.metaTitle,
          description: value.metaDescription,
          keywords: value.keywords,
          path: value.path,
          type: 'article',
          jsonLd: [
            localBusinessJsonLd(),
            articleJsonLd(value),
            faqJsonLd(value.faq),
            breadcrumbJsonLd([
              { name: 'Головна', path: '/' },
              { name: 'Блог', path: '/blog' },
              { name: value.h1, path: value.path }
            ])
          ]
        });
      },
      { immediate: true }
    );

    return {
      article,
      relatedServicePages,
      relatedArticles
    };
  }
};
</script>

<style scoped>
.article-page {
  display: grid;
  gap: 20px;
}

.article-hero,
.content-card,
.article-faq,
.article-links-box,
.article-cta {
  background: #fff;
  border: 1px solid #dde4ff;
  border-radius: 22px;
  box-shadow: 0 10px 28px rgba(46, 44, 97, 0.06);
}

.article-hero,
.article-faq,
.article-links-box,
.article-cta {
  padding: 28px;
}

.article-breadcrumbs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
  font-size: 13px;
}

.article-breadcrumbs a {
  color: #534ab7;
  text-decoration: none;
  font-weight: 700;
}

.article-date {
  margin-bottom: 12px;
  color: #6e78a2;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.article-hero h1 {
  margin: 0 0 12px;
  color: #202868;
  font-size: 38px;
  line-height: 1.1;
}

.article-excerpt {
  margin: 0;
  color: #55607a;
  font-size: 17px;
  line-height: 1.8;
}

.article-keywords {
  margin-top: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.keyword-chip {
  padding: 6px 10px;
  border-radius: 999px;
  background: #f4f7ff;
  color: #425181;
  font-size: 12px;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.article-content {
  display: grid;
  gap: 16px;
}

.content-card {
  padding: 24px;
}

.content-card h2,
.article-faq h2,
.article-links-head h2,
.article-cta h2 {
  margin: 0 0 12px;
  color: #202868;
  font-size: 28px;
}

.content-card p,
.faq-card p,
.related-card span,
.article-cta p {
  margin: 0;
  color: #55607a;
  line-height: 1.8;
  overflow-wrap: anywhere;
}

.faq-grid,
.article-links {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.faq-card {
  padding: 18px;
  border-radius: 18px;
  background: #f7f8ff;
  border: 1px solid #e1e6ff;
}

.faq-card h3 {
  margin: 0 0 10px;
  color: #202868;
  font-size: 20px;
}

.article-links-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin-bottom: 14px;
}

.article-more-link {
  color: #534ab7;
  text-decoration: none;
  font-size: 14px;
  font-weight: 800;
}

.article-link-grid {
  display: grid;
  gap: 12px;
}

.related-card {
  display: block;
  padding: 16px;
  border-radius: 16px;
  background: #f7f8ff;
  border: 1px solid #e1e6ff;
  text-decoration: none;
  min-width: 0;
}

.related-card strong {
  display: block;
  margin-bottom: 8px;
  color: #202868;
  font-size: 16px;
}

.article-cta {
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 20px;
  align-items: center;
}

.article-cta-actions {
  display: grid;
  gap: 12px;
}

.cta-btn {
  min-height: 50px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-size: 15px;
  font-weight: 800;
}

.cta-btn-primary {
  background: linear-gradient(135deg, #534ab7, #7f77dd);
  color: #fff;
}

.cta-btn-secondary {
  background: #edf5ff;
  color: #245bca;
}

@media (max-width: 960px) {
  .faq-grid,
  .article-links,
  .article-cta {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .article-hero h1 {
    font-size: 30px;
  }

  .content-card {
    padding: 20px;
  }

  .article-hero,
  .article-faq,
  .article-links-box,
  .article-cta {
    padding: 22px;
  }

  .article-links-head {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
